import { useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { useTheme } from '@mui/material';
import { convertsCentsToCurrency, convertsCurrencyToCents } from '@npi/buddy-utils';
import dayjs from 'dayjs';
import { Formik } from 'formik';

import { useParams } from '@redwoodjs/router';

import { InfoBanner } from 'src/components';
import { Flex } from 'src/components/Flex';
import { showErrorToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { Hierarchy } from 'src/constants';
import useReleaseSalesInvoice from 'src/features/finance/salesInvoice/hooks/useReleaseSalesInvoice';
import useSalesInvoiceResubmitToSage from 'src/features/finance/salesInvoice/hooks/useResubmitToSageSalesInvoice';
import useSalesInvoice from 'src/features/finance/salesInvoice/hooks/useSalesInvoice';
import invoiceInfoBannerConfigs from 'src/features/finance/salesInvoice/utils';
import ViewSalesInvoiceCallToActions from 'src/features/finance/salesInvoice/view/components/ViewSalesInvoiceCallToActions/ViewSalesInvoiceCallToActions';
import { BillingDetails } from 'src/features/finance/sepaDirectDebits/components';
import { GET_SALES_INVOICE_PDF } from 'src/graphql/queries/getSalesInvoicePDF.query';
import { useAllocateInvoicePayment } from 'src/hooks';
import { useCurrency } from 'src/hooks/useCurrency';
import { PageHeader } from 'src/layouts/MainLayout/PageHeader';
import { LogError } from 'src/lib';
import findBillingContact from 'src/utils/findBillingContact';
import { AllocatePaymentValidation } from 'src/validation/SalesInvoice';

import {
    CommentsSection,
    InvoiceDetails,
    InvoiceHistory,
    InvoiceItems,
    InvoicePaymentHistory,
    RecurringInvoiceConfiguration,
    TotalAmounts,
} from './components';

const ViewSalesInvoicePage = () => {
    const theme = useTheme();
    const { id } = useParams();
    const [pdfFile, setPDFFile] = useState<File>();
    const [fileUrl, setFileUrl] = useState<string>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showPaymentAllocationModal, setShowPaymentAllocationModel] = useState(false);
    const today = dayjs();

    const {
        refetchSalesInvoice,
        salesInvoice,
        isSalesInvoiceProcessing,
        isAllowedToAllocatePaymentToSalesInvoice,
        isSalesInvoiceReleaseFailed,
        isSalesInvoiceEmailFailed,
        isSalesInvoiceCreateFailed,
    } = useSalesInvoice({ id });

    const { allocateInvoicePayment, isAllocatingPayment } = useAllocateInvoicePayment(id, 'SALES_INVOICE');

    const { currencySymbol } = useCurrency(
        salesInvoice?.partner?.countryDetails ||
            salesInvoice?.merchant?.application?.partner?.countryDetails
    );

    const { handleResubmitToSage } = useSalesInvoiceResubmitToSage({
        id: salesInvoice?.id,
        refetchSalesInvoice,
    });

    const { handleReleaseSalesInvoice } = useReleaseSalesInvoice({
        id: salesInvoice?.id,
        refetchSalesInvoice,
    });

    const [getSalesInvoicePDF] = useLazyQuery(GET_SALES_INVOICE_PDF, {
        onError(error) {
            showErrorToast(error.message ?? 'An error occurred retrieving the invoice from Sage');
        },
        onCompleted: async (data) => {
            const url = data?.viewPDFSalesInvoice?.previewUrl;
            try {
                const byteCharacters = atob(data?.viewPDFSalesInvoice?.pdfBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const file = new File([blob], 'sales_invoice.pdf', {
                    type: 'application/pdf',
                });

                setPDFFile(file);
                setFileUrl(url);
                setIsModalOpen(true);
            } catch (error) {
                LogError(error);
            }
        },
    });

    const bannerConfigs = invoiceInfoBannerConfigs(salesInvoice?.status, theme);

    const recipientName = useMemo(() => {
        const to = salesInvoice?.to ?? Hierarchy.PARTNER;

        if (to === Hierarchy.PARTNER) {
            return `${salesInvoice?.partner?.primaryContactInformation?.pci_firstName ?? ''} ${
                salesInvoice?.partner?.primaryContactInformation?.pci_lastName ?? ''
            }`;
        }
        return salesInvoice?.merchant?.application?.tradingName;
    }, [salesInvoice]);

    const billingInformation = useMemo(() => {
        const merchantPrincipal = findBillingContact(
            salesInvoice?.merchant?.application?.merchantInformation?.principals
        );
        const registeredAddress =
            salesInvoice?.merchant?.application?.merchantInformation?.aboutBusiness
                ?.registeredAddress;

        const to = salesInvoice?.to ?? Hierarchy.PARTNER;

        if (to === Hierarchy.PARTNER) {
            return {
                name: `${salesInvoice?.partner?.billingContactInformation?.bci_firstName ?? ''} ${
                    salesInvoice?.partner?.billingContactInformation?.bci_lastName ?? ''
                }`,
                addressLine1:
                    salesInvoice?.partner?.billingContactInformation?.bci_ra_addressLine1 ?? '',
                addressLine2:
                    salesInvoice?.partner?.billingContactInformation?.bci_ra_addressLine2 ?? '',
                city: salesInvoice?.partner?.billingContactInformation?.bci_ra_city ?? '',
                county: '',
                country: salesInvoice?.partner?.billingContactInformation?.bci_ra_country ?? '',
                eircode: salesInvoice?.partner?.billingContactInformation?.bci_ra_eircode ?? '',
                email: salesInvoice?.partner?.billingContactInformation?.bci_email ?? '',
                contactNumber:
                    salesInvoice?.partner?.billingContactInformation?.bci_mobilePhoneNumber ?? '',
            };
        }
        return {
            name: `${merchantPrincipal?.firstName} ${merchantPrincipal?.lastName}`,
            addressLine1: registeredAddress?.addressLine1,
            addressLine2: registeredAddress.addressLine2,
            city: registeredAddress?.townOrCity,
            county: registeredAddress?.county,
            country: registeredAddress?.country,
            eircode: registeredAddress?.eircode,
            email: merchantPrincipal?.email,
            contactNumber: merchantPrincipal?.mobileNumber,
        };
    }, [salesInvoice]);

    const billingAddress = useMemo(() => {
        const { addressLine1, addressLine2, city, country, county, eircode } = billingInformation;

        return [addressLine1, addressLine2, county, city, country, eircode];
    }, [billingInformation]);

    return (
        <>
            <Formik
                onSubmit={async (values, actions) => {
                    const { paymentAmount, paymentMethod, balance, date } = values;
                    const paymentAmountInCents = convertsCurrencyToCents(parseFloat(paymentAmount));
                    const balanceInCents = convertsCurrencyToCents(balance);
                    const totalInvoiceAmount = salesInvoice?.totalInvoiceAmount ?? 0;

                    await allocateInvoicePayment(
                        paymentAmountInCents,
                        paymentMethod,
                        balanceInCents,
                        date.toDate(),
                        totalInvoiceAmount
                    );
                    refetchSalesInvoice({ id });
                    setShowPaymentAllocationModel(false);
                    actions.resetForm();
                }}
                initialValues={{
                    status: salesInvoice?.status,
                    balance: convertsCentsToCurrency(salesInvoice?.totalOutstanding),
                    totalInvoiceAmount: convertsCentsToCurrency(
                        salesInvoice?.totalInvoiceAmount ?? 0
                    ),
                    paymentAmount: convertsCentsToCurrency(salesInvoice?.totalOutstanding).toFixed(
                        2
                    ),
                    paymentMethod: null,
                    date: today,
                }}
                enableReinitialize={!isAllocatingPayment}
                validationSchema={AllocatePaymentValidation}
            >
                {({ handleSubmit }) => {
                    return (
                        <Flex flexDirection="column" gap="24px">
                            <PageHeader
                                heading="View Sales Invoice"
                                callToActions={
                                    <ViewSalesInvoiceCallToActions
                                        refetchSalesInvoice={refetchSalesInvoice}
                                        handleResubmitToSage={handleResubmitToSage}
                                        handleReleaseSalesInvoice={handleReleaseSalesInvoice}
                                        isSalesInvoiceProcessing={isSalesInvoiceProcessing}
                                        isAllowedToAllocatePaymentToSalesInvoice={
                                            isAllowedToAllocatePaymentToSalesInvoice
                                        }
                                        isSalesInvoiceCreateFailed={isSalesInvoiceCreateFailed}
                                        isSalesInvoiceReleaseFailed={isSalesInvoiceReleaseFailed}
                                        isSalesInvoiceEmailFailed={isSalesInvoiceEmailFailed}
                                        setShowPaymentAllocationModel={
                                            setShowPaymentAllocationModel
                                        }
                                        showPaymentAllocationModal={showPaymentAllocationModal}
                                        billingContactEmailAddress={billingInformation?.email}
                                        onAllocatePayment={handleSubmit}
                                        allocating={isAllocatingPayment}
                                        totalOutstanding={salesInvoice?.totalOutstanding}
                                        onView={async () => {
                                            try {
                                                await getSalesInvoicePDF({
                                                    variables: {
                                                        sageInvoiceId: salesInvoice?.sageInvoiceId,
                                                    },
                                                });
                                            } catch (error) {
                                                showErrorToast(
                                                    error.message ??
                                                        'An error occurred retrieving the invoice from Sage'
                                                );
                                            }
                                        }}
                                    />
                                }
                            />
                            {bannerConfigs ? (
                                <InfoBanner
                                    title={bannerConfigs?.title}
                                    subText={bannerConfigs?.subText}
                                    styles={bannerConfigs?.styles}
                                />
                            ) : null}

                            <Flex gap="inherit">
                                <InvoiceDetails
                                    billingCycle={salesInvoice?.billingCycle}
                                    invoiceDate={dayjs(salesInvoice?.date).toDate()}
                                    invoiceNumber={salesInvoice?.sageInvoiceNumber}
                                    invoiceStatus={salesInvoice?.status}
                                    recipientName={recipientName}
                                    recipientType={salesInvoice?.to}
                                />
                                <Flex flexDirection="column" flex={1.5} gap="24px">
                                    <BillingDetails
                                        billingAddress={billingAddress}
                                        contactNumber={billingInformation.contactNumber}
                                        name={billingInformation.name}
                                        email={billingInformation.email}
                                    />
                                    <TotalAmounts
                                        totalInvoiceAmount={convertsCentsToCurrency(
                                            salesInvoice?.totalInvoiceAmount ?? 0
                                        )}
                                        totalOutstanding={convertsCentsToCurrency(
                                            salesInvoice?.totalOutstanding ?? 0
                                        )}
                                        totalNetAmount={convertsCentsToCurrency(
                                            salesInvoice?.totalNetAmount ?? 0
                                        )}
                                        totalVatAmount={convertsCentsToCurrency(
                                            salesInvoice?.totalVatAmount ?? 0
                                        )}
                                        usersCurrency={currencySymbol}
                                    />
                                </Flex>
                            </Flex>
                            <InvoiceItems
                                lineItems={salesInvoice?.invoiceItems}
                                usersCurrency={currencySymbol}
                            />
                            <RecurringInvoiceConfiguration
                                invoiceFrequency={salesInvoice?.frequency}
                                invoiceOccurrence={salesInvoice?.occurrence}
                                invoiceOccurrenceType={salesInvoice?.occurrenceType}
                                recurringStartDate={new Date(salesInvoice?.recurringStartDate)}
                            />
                            <CommentsSection comments={salesInvoice?.salesInvoiceReason} />
                            <InvoiceHistory invoice={salesInvoice} />
                            <InvoicePaymentHistory
                                invoice={salesInvoice}
                                refetchInvoice={() => {
                                    return refetchSalesInvoice({ id });
                                }}
                            />
                        </Flex>
                    );
                }}
            </Formik>

            <ViewPDFModal
                title="View Sales Invoice PDF"
                file={pdfFile}
                fileURL={fileUrl}
                isModalOpen={isModalOpen}
                setIsModalOpen={() => {
                    return setIsModalOpen(false);
                }}
                maxWidth="lg"
                dialogStylesSX={{ padding: '8px' }}
            />
        </>
    );
};
export default ViewSalesInvoicePage;
